// src/components/ProductCard.js
import React, { memo, useState, useEffect } from 'react';
import usePrefetch, { createComponentPrefetcher } from '../hooks/usePrefetch';
import OptimizedImage from './OptimizedImage';
import { useUser } from '../context/UserContext';
import API from '../api';
import './ProductCard.css';

// Use a local base64 placeholder to avoid external requests during prerendering
const PLACEHOLDER_IMAGE = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjIwIiBoZWlnaHQ9IjIyMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjIwIiBoZWlnaHQ9IjIyMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjExMCIgeT0iMTIwIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTVweCIgZmlsbD0iIzk5OTk5OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';

// Create a prefetcher for the ProductDetail component
const productDetailPrefetcher = createComponentPrefetcher(
  () => import('../pages/ProductDetail')
);

/**
 * Product Card component with performance optimizations and love functionality
 */
const ProductCard = ({ product, countryCode, handleProductClick, onLoveToggle }) => {
  // Add prefetching when card becomes visible
  const prefetchRef = usePrefetch(productDetailPrefetcher);
  
  // User context and love state
  const { user } = useUser();
  const [isLoved, setIsLoved] = useState(false);
  const [showLoginMessage, setShowLoginMessage] = useState(false);

  // Check if the current product is loved by the user
  useEffect(() => {
    const checkLoveStatus = async () => {
      if (user && product?._id) {
        try {
          const response = await API.get(`/api/loved-products/check/${product._id}/${user.email}`, {
            headers: {
              'Accept': 'application/json'
            },
            timeout: 5000,
            skipCache: true
          });
          
          if (response.data && typeof response.data === 'object') {
            setIsLoved(!!response.data.isLoved);
          } else {
            setIsLoved(false);
          }
        } catch (err) {
          // Silently fail - not critical for card display
          setIsLoved(false);
        }
      }
    };
    
    checkLoveStatus();
  }, [user, product]);

  // Handle clicking the love button
  const handleLoveClick = async (e) => {
    e.stopPropagation(); // Prevent card click
    
    if (!user) {
      setShowLoginMessage(true);
      setTimeout(() => setShowLoginMessage(false), 3000);
      return;
    }

    try {
      // Toggle the state immediately for better UX
      const newLovedState = !isLoved;
      setIsLoved(newLovedState);
      
      const response = await API.post('/api/loved-products/toggle', 
        {
          productId: product._id,
          email: user.email
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          timeout: 8000
        }
      );
      
      if (response.data && typeof response.data === 'object') {
        const finalLovedState = response.data.isLoved;
        setIsLoved(finalLovedState);
        
        // Notify parent component about the love toggle
        if (onLoveToggle && typeof onLoveToggle === 'function') {
          onLoveToggle(product._id, finalLovedState);
        }
      }
    } catch (err) {
      console.error('Error toggling love status:', err);
      // Revert state on error
      setTimeout(() => {
        setIsLoved(!isLoved);
      }, 500);
    }
  };

  // Generate a descriptive alt text for the product image
  const getProductAlt = (product) => {
    return `${product.name} - ${product.description ? product.description.substring(0, 60) + (product.description.length > 60 ? '...' : '') : 'Authentic handcrafted product'}`;
  };

  // Format price to exactly match screenshot: "CAD $30.00" with proper spacing
  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    
    // Format price to match exact format in Products page
    const formattedPrice = Math.abs(price).toFixed(2);
    
    // Don't display negative prices or zero prices
    if (price === -1 || price === 0) {
      return ''; // Return empty string for price -1 or 0
    } else if (price < 0) {
      return `CAD -$${formattedPrice}`; 
    } else {
      return `CAD $${formattedPrice}`;
    }
  };

  // Always show a price - if it's 0 or missing but displayPrice flag is true, show as 'CAD $0.00'
  const shouldDisplayPrice = product.displayPrice !== false && product.price !== -1 && product.price !== 0 && product.price > 0;
  const displayPrice = product.price || 0;

  return (
    <div 
      ref={prefetchRef}
      className="product-card"
      onClick={() => handleProductClick(product)}
      tabIndex="0"
      role="button"
      aria-label={`View details for ${product.name}`}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleProductClick(product);
        }
      }}
    >
      {/* Mobile-optimized image container */}
      <div className="product-image" style={{
          margin: 0,
          padding: 0,
          aspectRatio: '1/1', // Explicit aspect ratio
          height: 'auto',
          width: '100%',
          position: 'relative' // Enable positioning for love button overlay
        }}>
        <OptimizedImage
          src={product.imageUrl || PLACEHOLDER_IMAGE}
          alt={getProductAlt(product)}
          className="product-img"
          width="300"
          height="300"
          priority={false} // Set to false to enable lazy loading
          loading="lazy"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = PLACEHOLDER_IMAGE;
          }}
          // Simplified to ensure consistent behavior
          sizes="(max-width: 768px) 150px, 300px"
        />
        
        {/* Love button overlay */}
        <button 
          className={`product-love-button ${isLoved ? 'loved' : ''}`}
          onClick={handleLoveClick}
          aria-label={isLoved ? 'Remove from favorites' : 'Add to favorites'}
        >
          <svg 
            viewBox="0 0 24 24" 
            width="16" 
            height="16" 
            fill={isLoved ? "#E32D2D" : "none"} 
            stroke={isLoved ? "#E32D2D" : "#666"}
            strokeWidth="2"
          >
            <path d="m12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z"/>
          </svg>
        </button>

        {/* Login message for unauthenticated users */}
        {showLoginMessage && (
          <div className="product-card-login-message" style={{
            position: 'absolute',
            top: '45px',
            right: '8px',
            background: 'rgba(0, 0, 0, 0.8)',
            color: 'white',
            padding: '8px 12px',
            borderRadius: '4px',
            fontSize: '0.8rem',
            zIndex: 3,
            whiteSpace: 'nowrap',
            maxWidth: '200px',
            textAlign: 'center'
          }}>
            Please sign in to add favorites
          </div>
        )}
        
        {product.origin && (
          <div className="product-origin">
            <span>{product.origin}</span>
          </div>
        )}
      </div>
      <div className="product-info">
        <h3 title={product.name}>{product.name}</h3>
        
        {/* Show price only if it's not -1 or 0 */}
        {shouldDisplayPrice ? (
          <p className="product-price">
            {formatPrice(displayPrice)}
          </p>
        ) : (
          // Empty div with same height to maintain spacing when price is hidden
          <div className="price-spacer" style={{
            height: '1.2rem', // Reduced from 1.5rem
            marginBottom: '0.5rem', // Reduced from 0.75rem
            visibility: 'hidden'
          }}></div>
        )}
        
        {/* Two-button layout: Buy and Details */}
        <div className="product-buttons-container" style={{
          display: 'flex',
          gap: '8px',
          marginTop: 'auto'
        }}>
          {(() => {
            // Check if any purchase links are available
            const hasAmazonLink = product.affiliateLinks?.some(link => 
              link.name?.toLowerCase().includes('amazon')
            );
            const hasAffiliateLinks = product.affiliateLinks?.length > 0;
            const hasStoreLink = product.storeLink;
            const hasPurchaseLink = hasAmazonLink || hasAffiliateLinks || hasStoreLink;
            
            return (
              <button
                className="buy-btn"
                style={{
                  backgroundColor: hasPurchaseLink ? 'var(--primary-color, #E32D2D)' : '#cccccc',
                  color: 'white',
                  textAlign: 'center',
                  padding: '10px 16px',
                  borderRadius: '6px',
                  border: 'none',
                  fontWeight: '500',
                  fontSize: '0.9rem',
                  flex: '1',
                  cursor: hasPurchaseLink ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  opacity: hasPurchaseLink ? 1 : 0.6,
                  fontFamily: 'inherit',
                  boxShadow: hasPurchaseLink ? '0 2px 4px rgba(227, 45, 45, 0.2)' : 'none',
                  position: 'relative',
                  overflow: 'hidden',
                }}
                disabled={!hasPurchaseLink}
                onClick={(e) => {
                  e.stopPropagation();
                  
                  if (!hasPurchaseLink) return;
                  
                  // Check for Amazon link first, then any affiliate link, then store link
                  const amazonLink = product.affiliateLinks?.find(link => 
                    link.name?.toLowerCase().includes('amazon')
                  );
                  
                  let targetLink, linkType, linkName;
                  
                  if (amazonLink) {
                    targetLink = amazonLink.url;
                    linkType = 'amazon';
                    linkName = amazonLink.name || 'Amazon';
                  } else if (product.affiliateLinks?.length > 0) {
                    targetLink = product.affiliateLinks[0].url;
                    linkType = 'affiliate';
                    linkName = product.affiliateLinks[0].name || 'Affiliate';
                  } else if (product.storeLink) {
                    targetLink = product.storeLink;
                    linkType = 'store';
                    linkName = 'Store';
                  }
                  
                  // Track in Google Analytics
                  if (window.gtag) {
                    window.gtag('event', 'click', {
                      event_category: 'Ecommerce',
                      event_label: `Buy on ${linkName}: ${product.name}`,
                      event_action: 'product_card_buy_click',
                      item_id: product._id,
                      item_name: product.name,
                      item_category: product.category || 'Uncategorized',
                      item_variant: linkName,
                      price: product.price || 0,
                      affiliate_platform: linkType === 'amazon' ? 'Amazon' : linkName,
                      currency: countryCode === 'CA' ? 'CAD' : 'USD',
                      source: 'product_card'
                    });
                  }
                  
                  // Open the link
                  if (targetLink) {
                    window.open(targetLink, '_blank', 'noopener,noreferrer');
                  }
                }}
                onMouseEnter={(e) => {
                  if (hasPurchaseLink) {
                    e.target.style.backgroundColor = '#c41e3a';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 8px rgba(227, 45, 45, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (hasPurchaseLink) {
                    e.target.style.backgroundColor = 'var(--primary-color, #E32D2D)';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 4px rgba(227, 45, 45, 0.2)';
                  }
                }}
              >
                Buy
              </button>
            );
          })()}
          
          <button
            className="detail-btn"
            style={{
              backgroundColor: 'white',
              color: 'var(--primary-color, #E32D2D)',
              textAlign: 'center',
              padding: '10px 16px',
              borderRadius: '6px',
              border: '2px solid var(--primary-color, #E32D2D)',
              fontWeight: '500',
              fontSize: '0.9rem',
              flex: '1',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              fontFamily: 'inherit',
              boxShadow: '0 2px 4px rgba(227, 45, 45, 0.1)',
              position: 'relative',
              overflow: 'hidden',
            }}
            onClick={(e) => {
              e.stopPropagation();
              
              // Track in Google Analytics
              if (window.gtag) {
                window.gtag('event', 'click', {
                  event_category: 'Product Interaction',
                  event_label: `View Details: ${product.name}`,
                  event_action: 'product_card_details_click',
                  item_id: product._id,
                  item_name: product.name,
                  item_category: product.category || 'Uncategorized',
                  source: 'product_card'
                });
              }
              
              handleProductClick(product);
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = 'var(--primary-color, #E32D2D)';
              e.target.style.color = 'white';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(227, 45, 45, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'white';
              e.target.style.color = 'var(--primary-color, #E32D2D)';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(227, 45, 45, 0.1)';
            }}
          >
            Details
          </button>
        </div>
      </div>
    </div>
  );
};

// Use React.memo to prevent unnecessary re-renders
export default memo(ProductCard);